import { Box, Flex } from '@mantine/core';
import { useMemo, useState } from 'react';
import { SiestaChoicesSummary } from './SiestaChoicesSummary';
import { SiestaStepCounter } from 'src/UI/generate-flow/SiestaStepCounter';
import { SiestaStepCard } from 'src/UI/generate-flow/SiestaStepCard';
import { SiestaSummaryCard } from '../components/SiestaSummaryCard';
import {
  ProcessingSidebarAnimation,
  type SummaryItem,
} from 'src/UI/generate-flow/ProcessingSidebarAnimation';
import { useOneAdvisor } from '../../hooks/useOneAdvisor';
import { useSiestaSummary } from '../../hooks/useSiestaSummary';
import { NewSuitabilityData, StepId } from '../../types';

type StepDef = {
  id: StepId;
  title: string;
};

type Props = {
  stepIndex: number;
  steps: StepDef[];
  data: NewSuitabilityData;
  currentStepId: StepId;
  setData: React.Dispatch<React.SetStateAction<NewSuitabilityData>>;
  isReportSubmitting?: boolean;
};

export function SuitabilitySidebar({
  stepIndex,
  steps,
  data,
  currentStepId,
  setData,
  isReportSubmitting = false,
}: Props) {
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [isFullscreenExpanded, setIsFullscreenExpanded] = useState(false);

  const clientAdvisor = data.isFamily ? data.family?.advisorUserId : data.client?.advisorUserId;
  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;

  const { advisor } = useOneAdvisor(clientAdvisor);

  const summary = useSiestaSummary(currentStepId, data, setData);
  const hasSpaceForExpansion = !summary;

  const isQuickSummaryStep =
    currentStepId === 'advice' || currentStepId === 'documents' || currentStepId === 'support';

  const visibleChoicesCount = useMemo(() => {
    if (stepIndex < 1) return 0;

    const choices = [
      { step: 'advice', hasData: !!clientId },
      { step: 'documents', hasData: data.adviceFiles.length > 0 },
      { step: 'template', hasData: data.feeDocuments.length > 0 },
      { step: 'support', hasData: !!data.template },
    ] as const;

    return choices.filter(({ step, hasData }) => {
      const stepIdx = steps.findIndex(s => s.id === step);
      return stepIdx !== -1 && stepIdx <= stepIndex && hasData;
    }).length;
  }, [
    stepIndex,
    steps,
    clientId,
    data.adviceFiles.length,
    data.feeDocuments.length,
    data.template,
  ]);

  const hasSelection = useMemo(() => {
    switch (currentStepId) {
      case 'client':
        return !!clientId;

      case 'advice':
        return data.adviceFiles.length > 0;

      case 'documents':
        return data.feeDocuments.length > 0;

      case 'template':
        return !!data.template;

      case 'support':
        return data.supportDocuments.length > 0;

      default:
        return false;
    }
  }, [
    currentStepId,
    clientId,
    data.adviceFiles.length,
    data.feeDocuments.length,
    data.template,
    data.supportDocuments.length,
  ]);

  const clientName = data.isFamily
    ? `${data.family?.name} family`
    : `${data.client?.firstName} ${data.client?.lastName}`;

  if (currentStepId === 'processing') {
    const summaryItems: SummaryItem[] = [
      {
        id: 'client',
        label: 'Client',
        value: clientName,
        extraLabel: advisor?.firstName ? `Advisor: ${advisor.firstName}` : undefined,
      },
      {
        id: 'advice',
        label: 'Advice',
        value:
          data.adviceFiles.length > 0
            ? data.adviceFiles.map(f => f.name).join(', ')
            : 'No documents selected',
      },
      {
        id: 'fees',
        label: 'Fee documents',
        value:
          data.feeDocuments.length > 0
            ? data.feeDocuments.map(f => f.name).join(', ')
            : 'No documents selected',
      },
      ...(data.template
        ? [{ id: 'template', label: 'Template', value: data.template.displayName }]
        : []),
      {
        id: 'support',
        label: 'Supporting documents',
        value:
          data.supportDocuments.length > 0
            ? data.supportDocuments.map(f => f.name).join(', ')
            : 'No documents selected',
      },
    ];

    return (
      <ProcessingSidebarAnimation
        items={summaryItems}
        clientFirstName={data.isFamily ? data.family?.name : data.client?.firstName}
        documentLabel="Suitability report"
        estimatedTime="~2 min"
        isSubmitting={isReportSubmitting}
        navigateTo={`/client/${clientId}/document/suitability-reports`}
      />
    );
  }

  return (
    <Flex
      direction="column"
      h="100%"
      pos="relative"
      style={{ transition: 'all 300ms ease-in-out' }}
    >
      {stepIndex >= 1 && !isFullscreenExpanded && (
        <Flex justify="center" style={{ flexShrink: 0, transition: 'opacity 300ms ease-in-out' }}>
          <SiestaChoicesSummary
            currentStep={stepIndex}
            clientName={clientName}
            advisorName={advisor?.firstName}
            templateName={data.template?.displayName}
            reportFormat={data.reportFormat}
            adviceFiles={data.adviceFiles}
            feeDocuments={data.feeDocuments}
            supportDocuments={data.supportDocuments}
            onExpandedChange={(_, isHover) => {
              setIsHoverExpanded(isHover);
            }}
            forceExpanded={hasSpaceForExpansion}
          />
        </Flex>
      )}

      <Flex
        direction="column"
        align="center"
        justify={summary ? 'flex-start' : 'center'}
        flex={1}
        mih={0}
      >
        {!isFullscreenExpanded && (
          <Flex
            direction="column"
            align="center"
            gap="xs"
            pt={stepIndex >= 1 && summary ? 'lg' : 0}
            style={{
              marginTop: !summary && visibleChoicesCount > 0 ? `-${visibleChoicesCount * 32}px` : 0,
              transition: 'all 300ms ease-in-out',
            }}
          >
            <SiestaStepCounter currentStep={stepIndex + 1} totalSteps={steps.length - 1} />
            <SiestaStepCard
              steps={steps}
              currentStep={stepIndex + 1}
              dimmed={isHoverExpanded}
              minimized={hasSelection}
            />
          </Flex>
        )}

        {summary && (
          <>
            {!isFullscreenExpanded && <Box style={{ flex: '0.35 1 0' }} mih="lg" />}
            <Flex
              direction="column"
              align="center"
              opacity={isHoverExpanded && !isFullscreenExpanded ? 0.7 : 1}
              style={{
                flex: '1 1 0',
                minHeight: 0,
                maxHeight: '100%',
                transition: 'opacity 300ms ease-in-out, padding 300ms ease-in-out',
              }}
              w="100%"
              pt={isFullscreenExpanded ? 'md' : 0}
            >
              <Flex
                justify="center"
                w="100%"
                style={{ maxHeight: '100%', transition: 'all 300ms ease-in-out' }}
              >
                <SiestaSummaryCard
                  width={isFullscreenExpanded ? 420 : 374}
                  title={summary.title}
                  headline={summary.headline}
                  subline={summary.subline}
                  sections={summary.sections}
                  showDetailsButton={isQuickSummaryStep}
                  onAnyExpanded={setIsFullscreenExpanded}
                />
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </Flex>
  );
}
